import SearchIcon from '@mui/icons-material/Search';
import {
  Card,
  CardContent,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { differenceInDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useState } from 'react';
import { LaneFilterState } from 'src/entities/ShipmentOrderLane';

type Props = {
  filters: LaneFilterState;
  onFiltersChange: (newFilters: Partial<LaneFilterState>) => void;
  onDateRangeChange: (startOn: string, endOn: string) => void;
};

// ステータスオプション
const planningStatusOptions = [
  { value: '', label: 'すべてのステータス' },
  { value: 'not_planned', label: '未配車' },
  { value: 'partial', label: '一部配車済' },
  { value: 'completed', label: '配車完了' },
];

const deliveryMethodOptions = [
  { value: '', label: 'すべての配送区分' },
  { value: '貸切', label: '貸切' },
  { value: '共配', label: '共配' },
  { value: '路線便', label: '路線便' },
]

const berthReservationOptions = [
  { value: '', label: 'すべてのバース予約' },
  { value: 'reserved', label: '予約済' },
  { value: 'partial', label: '一部予約済' },
  { value: 'not_reserved', label: '未予約' },
  { value: 'not_required', label: '不要' },
]

/**
 * レーンフィルタコンポーネント
 */
const LaneFilters: FC<Props> = memo(({
  filters,
  onFiltersChange,
  onDateRangeChange,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [keywordInput, setKeywordInput] = useState(filters.keyword || '');
  const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([
    new Date(filters.loadingStartOn),
    new Date(filters.loadingEndOn)
  ]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateRange = useCallback((range: DateRange<Date>) => {
    setSelectedRange((prev) => [
      range[0] || prev[0],
      range[1] || prev[1],
    ]);
    const start = range[0];
    const end = range[1];
    if (!start || !end) return;

    const diffDays = differenceInDays(end, start);

    if (diffDays < 0) {
      enqueueSnackbar('終了日は開始日よりも過去に設定できません');
      return;
    }

    if (diffDays > 9) {
      enqueueSnackbar('最大取得可能日数の10日を超過しています');
      return;
    }
    onDateRangeChange(formatDate(start), formatDate(end));
  }, [enqueueSnackbar, onDateRangeChange]);

  // ステータス変更
  const handlePlanningStatusChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFiltersChange({ planningStatus: value || undefined });
  }, [onFiltersChange]);

  const handleDeliveryMethodChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFiltersChange({ deliveryMethod: value || undefined });
  }, [onFiltersChange]);

  const handleBerthReservationChange = useCallback((event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    onFiltersChange({ berthReservation: value || undefined });
  }, [onFiltersChange]);

  // キーワード変更（Enterキーで検索）
  const handleKeywordKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onFiltersChange({ keyword: keywordInput || undefined });
    }
  }, [keywordInput, onFiltersChange]);

  // キーワード入力変更
  const handleKeywordChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setKeywordInput(event.target.value);
  }, []);

  // キーワードフォーカスアウトで検索
  const handleKeywordBlur = useCallback(() => {
    if (keywordInput !== (filters.keyword || '')) {
      onFiltersChange({ keyword: keywordInput || undefined });
    }
  }, [keywordInput, filters.keyword, onFiltersChange]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {/* キーワード検索 */}
          <TextField
            size="small"
            placeholder="キーワード検索"
            value={keywordInput}
            onChange={handleKeywordChange}
            onKeyDown={handleKeywordKeyDown}
            onBlur={handleKeywordBlur}
            sx={{ minWidth: 200, width: '30vw' }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }
            }}
          />

          {/* 日付範囲 */}
          <Stack direction="row" spacing={1} alignItems="center">
            <DateRangePicker
              value={selectedRange}
              onChange={handleDateRange}
              slotProps={{
                textField: { size: 'small', sx: { p: 0, m: 0, }}
              }}
            />
          </Stack>

          {/* ステータスフィルタ */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>ステータス</InputLabel>
            <Select
              value={filters.planningStatus || ''}
              label="ステータス"
              onChange={handlePlanningStatusChange}
            >
              {planningStatusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 配送区分フィルタ */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>配送区分</InputLabel>
            <Select
              value={filters.deliveryMethod || ''}
              label="配送区分"
              onChange={handleDeliveryMethodChange}
            >
              {deliveryMethodOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* バース予約 */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>バース予約</InputLabel>
            <Select
              value={filters.berthReservation || ''}
              label="バース予約"
              onChange={handleBerthReservationChange}
            >
              {berthReservationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </CardContent>
    </Card>
  );
});

export default LaneFilters;
